import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import { useAuth } from '@/shared/auth';
import { toaster } from '@/shared/libs';
import { Button } from '@/shared/ui';
import {
  useArchiveDealMutation,
  useGenerateDealLinkMutation,
  useRevokeDealMutation,
  useTerminateDealMutation,
} from '@entities/Deal';
import type { Deal, DealStatus, UserRole } from '@psp-aggregator/sdk';

interface DealTicketActionsProps {
  deal: Deal;
}

type DealAction = 'archive' | 'generateLink' | 'revoke' | 'terminate';

const ACTIONS_BY_STATUS: Record<DealStatus, readonly DealAction[]> = {
  agreed: ['terminate'],
  archived: [],
  declined: ['archive'],
  draft: ['generateLink'],
  expired: ['archive'],
  in_review: ['revoke', 'terminate'],
  revoked: ['archive'],
  terminated: ['archive'],
};

const INTERNAL_DEAL_ROLES = new Set<UserRole>(['super_admin', 'admin']);

export const DealTicketActions: FC<DealTicketActionsProps> = ({ deal }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const generateLink = useGenerateDealLinkMutation();
  const revoke = useRevokeDealMutation();
  const terminate = useTerminateDealMutation();
  const archive = useArchiveDealMutation();
  const actions = ACTIONS_BY_STATUS[deal.status];
  const canManage = user?.role ? INTERNAL_DEAL_ROLES.has(user.role) : false;

  if (!canManage || actions.length === 0) {
    return null;
  }

  const handleAction = async (action: DealAction): Promise<void> => {
    try {
      if (action === 'generateLink') {
        const link = await generateLink.mutateAsync(deal.id);

        await navigator.clipboard.writeText(link.url);
        toaster.success({ title: t('toast.dealLinkGenerated') });

        return;
      }

      const variables = { dealId: deal.id, payload: {} };
      const mutationByAction = {
        archive,
        revoke,
        terminate,
      };

      await mutationByAction[action].mutateAsync(variables);
      toaster.success({ title: t('toast.dealUpdated') });
    } catch (error) {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ?? t('errors.deals.actionFailed'))
          : t('errors.deals.actionFailed'),
      });
    }
  };

  const labelByAction: Record<DealAction, string> = {
    archive: t('pages.admin.deals.actions.archive'),
    generateLink: t('pages.admin.deals.actions.generateLink'),
    revoke: t('pages.admin.deals.actions.revoke'),
    terminate: t('pages.admin.deals.actions.terminate'),
  };

  return (
    <div className="flex flex-wrap justify-end gap-2">
      {actions.map((action) => (
        <Button
          key={action}
          loading={
            generateLink.isPending ||
            revoke.isPending ||
            terminate.isPending ||
            archive.isPending
          }
          type="button"
          variant={action === 'terminate' ? 'destructive' : 'secondary'}
          onClick={() => {
            void handleAction(action);
          }}>
          {labelByAction[action]}
        </Button>
      ))}
    </div>
  );
};

DealTicketActions.displayName = 'DealTicketActions';
